import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import {
  buildHistoryParams,
  getHistoryDateTimeDisplay,
  loadPopupHistory
} from 'src/app/_helpers/history-popup.helper';
import { getHistoryChangedFields } from 'src/app/_helpers/history-changed-fields.helper';
import { JobDictionaryRecordHistory } from 'src/app/models/jobDictionaryRecordHistory.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { JobDictionaryService } from 'src/app/services/job-dictionary.service';
import { ToastService } from 'src/app/services/toast.service';

interface JobDictionaryHistoryDialogData {
  id: string;
  title: string;
}

@Component({
  standalone: false,
  selector: 'app-job-dictionary-history-dialog',
  templateUrl: './job-dictionary-history-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./job-dictionary-history-dialog.component.scss']
})
export class JobDictionaryHistoryDialogComponent implements OnInit, OnDestroy {

  displayedColumns = ['dateUpdated', 'updatedByUserName', 'code', 'name', 'description', 'isActive', 'reason'];
  historyDataSource = new MatTableDataSource<JobDictionaryRecordHistory>([]);
  isHistoryLoading = false;
  historyLoaded = false;

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<JobDictionaryHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: JobDictionaryHistoryDialogData,
    private jobDictionaryService: JobDictionaryService,
    private toastService: ToastService,
    private dateFormatService: DateFormatService
  ) { }

  ngOnInit(): void {
    this.getHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getHistory(): void {
    this.isHistoryLoading = true;
    const params = buildHistoryParams('jobDictionaryId', this.data.id);

    loadPopupHistory<JobDictionaryRecordHistory>(
      this.jobDictionaryService.getHistory(params),
      this.destroy$,
      (history) => this.mapHistoryRecord(history),
      (records) => {
        this.historyDataSource.data = records;
        this.historyLoaded = true;
        this.isHistoryLoading = false;
      },
      (error) => {
        this.toastService.showError(error);
        this.historyDataSource.data = [];
        this.historyLoaded = false;
        this.isHistoryLoading = false;
      }
    );
  }

  isChanged(history: JobDictionaryRecordHistory, field: string): boolean {
    return history.changedFieldSet?.has(field) ?? false;
  }

  close(): void {
    this.dialogRef.close();
  }

  private mapHistoryRecord(history: JobDictionaryRecordHistory): JobDictionaryRecordHistory {
    return {
      ...history,
      changedFieldSet: new Set(getHistoryChangedFields(history)),
      dateUpdatedDisplay: getHistoryDateTimeDisplay(history.dateUpdated, this.dateFormatService),
      statusDisplay: history.isActive ? 'Yes' : 'No'
    };
  }
}
